import React from 'react';
import Icon from '../../../components/AppIcon';

const AnalyticsPanel = ({ analyticsData, selectedCurrency }) => {
  const formatCurrency = (amount) => {
    const symbols = { USD: '$', EUR: '€', BTC: '₿', ETH: 'Ξ' };
    const decimals = selectedCurrency === 'BTC' || selectedCurrency === 'ETH' ? 6 : 2;
    return `${symbols?.[selectedCurrency]}${amount?.toLocaleString('en-US', { 
      minimumFractionDigits: decimals, 
      maximumFractionDigits: decimals 
    })}`;
  };

  const getRiskColor = (level) => {
    switch (level?.toLowerCase()) {
      case 'low': return 'text-success';
      case 'medium': return 'text-warning';
      case 'high': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const getRiskIcon = (level) => {
    switch (level?.toLowerCase()) {
      case 'low': return 'Shield';
      case 'medium': return 'AlertTriangle';
      case 'high': return 'AlertCircle';
      default: return 'Info';
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Diversification Metrics */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center space-x-2 mb-4">
          <Icon name="PieChart" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-foreground">Diversification</h3>
        </div>
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Number of Assets</span>
            <span className="font-medium text-foreground">{analyticsData?.diversification?.assetCount}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Largest Holding</span>
            <span className="font-medium text-foreground">{analyticsData?.diversification?.largestHolding}%</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Concentration Risk</span>
            <span className={`font-medium ${getRiskColor(analyticsData?.diversification?.concentrationRisk)}`}>
              {analyticsData?.diversification?.concentrationRisk}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Herfindahl Index</span>
            <span className="font-medium text-foreground">{analyticsData?.diversification?.herfindahlIndex}</span>
          </div>
        </div>
      </div>
      {/* Risk Assessment */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center space-x-2 mb-4">
          <Icon 
            name={getRiskIcon(analyticsData?.risk?.level)} 
            size={20} 
            className={getRiskColor(analyticsData?.risk?.level)} 
          />
          <h3 className="text-lg font-semibold text-foreground">Risk Assessment</h3>
        </div>
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Risk Level</span>
            <span className={`font-medium ${getRiskColor(analyticsData?.risk?.level)}`}>
              {analyticsData?.risk?.level}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Volatility (30d)</span>
            <span className="font-medium text-foreground">{analyticsData?.risk?.volatility}%</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Value at Risk (95%)</span>
            <span className="font-medium text-error">{formatCurrency(analyticsData?.risk?.valueAtRisk)}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Sharpe Ratio</span>
            <span className="font-medium text-foreground">{analyticsData?.risk?.sharpeRatio}</span>
          </div>
        </div>
      </div>
      {/* Correlation Analysis */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center space-x-2 mb-4">
          <Icon name="GitBranch" size={20} className="text-accent" />
          <h3 className="text-lg font-semibold text-foreground">Correlation Analysis</h3>
        </div>
        <div className="space-y-3">
          {analyticsData?.correlations?.map((correlation, index) => (
            <div key={index} className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">{correlation?.pair}</span>
              <div className="flex items-center space-x-2">
                <div className="w-16 bg-muted rounded-full h-2">
                  <div 
                    className="h-2 rounded-full bg-primary" 
                    style={{ width: `${Math.abs(correlation?.value) * 100}%` }}
                  />
                </div>
                <span className="font-medium text-foreground text-sm w-12 text-right">
                  {correlation?.value?.toFixed(2)}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Performance Metrics */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center space-x-2 mb-4">
          <Icon name="TrendingUp" size={20} className="text-success" />
          <h3 className="text-lg font-semibold text-foreground">Performance Metrics</h3>
        </div>
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Total Return</span>
            <span className="font-medium text-success">
              +{analyticsData?.performance?.totalReturn}%
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Annualized Return</span>
            <span className="font-medium text-foreground">
              {analyticsData?.performance?.annualizedReturn}%
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Max Drawdown</span>
            <span className="font-medium text-error">
              {analyticsData?.performance?.maxDrawdown}%
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Win Rate</span>
            <span className="font-medium text-foreground">
              {analyticsData?.performance?.winRate}%
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsPanel;